import { useEffect, useMemo, useRef, useState } from "react";
import { useData } from "@/context/DataContext";
import { formatTime, toMinutes } from "@/lib/date";
import { MapPin, Trash, PencilSimple, DotsSixVertical } from "@phosphor-icons/react";
import EventEditModal from "@/components/EventEditModal";
import { toast } from "sonner";
import { deleteWithUndo } from "@/lib/undo";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

const START_HOUR = 5;
const END_HOUR = 24; // exclusive display up to midnight
const HOUR_HEIGHT = 64; // px per hour
const SNAP_MINUTES = 15;
const DRAG_THRESHOLD_PX = 4;

export default function Timeline({ date, events }) {
  const { catById, deleteEvent, restoreEvent, skipOccurrence, unskipOccurrence, updateEvent, refresh } = useData();
  const [now, setNow] = useState(new Date());
  const [editEvent, setEditEvent] = useState(null);
  const [dragState, setDragState] = useState(null); // { id, deltaMin, startY }
  const [deleteTarget, setDeleteTarget] = useState(null); // ev to be deleted
  const draggingRef = useRef(null);

  const onDeleteRequest = (ev) => {
    const isVirtual = String(ev.id).includes("::");
    const isRecurring = (ev.recurrence && ev.recurrence !== "none") || isVirtual;
    if (isRecurring) {
      setDeleteTarget(ev);
    } else {
      // Non-recurring: delete with undo
      deleteWithUndo({
        label: `"${ev.title}" verwijderd`,
        deleteFn: async () => { await deleteEvent(ev.id); },
        restoreFn: async () => { await restoreEvent(ev); },
      });
    }
  };

  const skipOne = async (ev) => {
    setDeleteTarget(null);
    deleteWithUndo({
      label: `${ev.title} overgeslagen op ${ev.date}`,
      deleteFn: async () => { await skipOccurrence(ev.id, ev.date); },
      restoreFn: async () => { await unskipOccurrence(ev.id, ev.date); },
    });
  };

  const deleteSeries = async (ev) => {
    const parentId = String(ev.id).split("::")[0];
    const snapshot = (events || []).find((e) => e.id === parentId) || ev;
    setDeleteTarget(null);
    deleteWithUndo({
      label: `Reeks "${ev.title}" verwijderd`,
      deleteFn: async () => { await deleteEvent(parentId); },
      restoreFn: async () => { await restoreEvent(snapshot); },
    });
  };

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
    // setNow is a stable state setter; interval only depends on mount lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hours = useMemo(() => {
    const arr = [];
    for (let h = START_HOUR; h <= END_HOUR; h++) arr.push(h);
    return arr;
  }, []);

  const dayEvents = useMemo(
    () => (events || []).filter((e) => e.date === date).sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time)),
    [events, date]
  );

  // Assign lanes to overlapping events
  const laidOut = useMemo(() => {
    const items = dayEvents.map((e) => ({ ...e, _s: toMinutes(e.start_time), _e: Math.max(toMinutes(e.end_time), toMinutes(e.start_time) + 15) }));
    const lanes = []; // each lane = list of items; track end minute
    const laneEnds = []; // last end for each lane
    const withLane = items.map((it) => {
      let placed = -1;
      for (let i = 0; i < laneEnds.length; i++) {
        if (laneEnds[i] <= it._s) { placed = i; break; }
      }
      if (placed === -1) { placed = laneEnds.length; laneEnds.push(0); }
      laneEnds[placed] = it._e;
      return { ...it, lane: placed };
    });
    // Group by overlap cluster to compute total lanes at each block
    // Simpler: for each item find max concurrent lanes among items it overlaps with
    const totals = withLane.map((it) => {
      let maxLane = it.lane;
      for (const other of withLane) {
        if (other === it) continue;
        if (other._s < it._e && other._e > it._s) {
          if (other.lane > maxLane) maxLane = other.lane;
        }
      }
      return maxLane + 1;
    });
    return withLane.map((it, idx) => ({ ...it, totalLanes: totals[idx] }));
  }, [dayEvents]);

  const isToday = useMemo(() => {
    const d = new Date();
    const [y, m, dd] = date.split("-").map(Number);
    return d.getFullYear() === y && d.getMonth() + 1 === m && d.getDate() === dd;
  }, [date]);

  // Drag-to-reschedule / Resize
  useEffect(() => {
    const onMove = (clientY) => {
      const s = draggingRef.current;
      if (!s) return;
      const dy = clientY - s.startY;
      const rawMin = dy * (60 / HOUR_HEIGHT);
      const snapped = Math.round(rawMin / SNAP_MINUTES) * SNAP_MINUTES;
      let clampedDelta = snapped;
      if (s.mode === "resize") {
        // Resize end only
        const newEnd = s.origEnd + snapped;
        const minEnd = s.origStart + 15;
        if (newEnd < minEnd) clampedDelta = minEnd - s.origEnd;
        if (newEnd > END_HOUR * 60) clampedDelta = END_HOUR * 60 - s.origEnd;
      } else {
        // Move the whole block
        const newStart = s.origStart + snapped;
        const newEnd = newStart + s.duration;
        if (newStart < START_HOUR * 60) clampedDelta = START_HOUR * 60 - s.origStart;
        if (newEnd > END_HOUR * 60) clampedDelta = END_HOUR * 60 - s.origStart - s.duration;
      }
      draggingRef.current = { ...s, deltaMin: clampedDelta, moved: Math.abs(dy) > DRAG_THRESHOLD_PX || s.moved };
      setDragState({ id: s.id, deltaMin: clampedDelta, mode: s.mode });
    };
    const mm = (e) => onMove(e.clientY);
    const tm = (e) => { if (e.touches[0]) onMove(e.touches[0].clientY); };
    const end = async () => {
      const s = draggingRef.current;
      if (!s) return;
      draggingRef.current = null;
      setDragState(null);
      document.body.style.userSelect = "";
      if (!s.moved || s.deltaMin === 0) {
        if (!s.moved && s.mode !== "resize") setEditEvent(s.event);
        return;
      }
      try {
        if (s.mode === "resize") {
          const newEnd = s.origEnd + s.deltaMin;
          const endStr = `${String(Math.floor(newEnd / 60)).padStart(2, "0")}:${String(newEnd % 60).padStart(2, "0")}`;
          await updateEvent(s.event.id, { end_time: endStr });
          const durMin = newEnd - s.origStart;
          const h = Math.floor(durMin / 60);
          const m = durMin % 60;
          toast.success(`Resized to ${h ? `${h}h` : ""}${m ? ` ${m}m` : ""}`.trim() || "Resized");
        } else {
          const newStart = s.origStart + s.deltaMin;
          const newEnd = newStart + s.duration;
          const startStr = `${String(Math.floor(newStart / 60)).padStart(2, "0")}:${String(newStart % 60).padStart(2, "0")}`;
          const endStr = `${String(Math.floor(newEnd / 60)).padStart(2, "0")}:${String(newEnd % 60).padStart(2, "0")}`;
          await updateEvent(s.event.id, { start_time: startStr, end_time: endStr });
          toast.success(`Moved to ${formatTime(startStr)}`);
        }
      } catch {
        toast.error("Update failed");
      }
    };
    window.addEventListener("mousemove", mm);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", tm, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", mm);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", tm);
      window.removeEventListener("touchend", end);
    };
  }, [updateEvent]);

  const startDrag = (ev, clientY, mouseEvent, mode = "move") => {
    if (mouseEvent) mouseEvent.preventDefault();
    document.body.style.userSelect = "none";
    draggingRef.current = {
      id: ev.id,
      event: ev,
      startY: clientY,
      origStart: ev._s,
      origEnd: ev._e,
      duration: ev._e - ev._s,
      deltaMin: 0,
      moved: false,
      mode,
    };
    setDragState({ id: ev.id, deltaMin: 0, mode });
  };

  const currentTop = () => {
    const mins = now.getHours() * 60 + now.getMinutes();
    const base = START_HOUR * 60;
    if (mins < base || mins > END_HOUR * 60) return null;
    return ((mins - base) / 60) * HOUR_HEIGHT;
  };

  const totalHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT;

  return (
    <div className="rounded-xl border border-slate-800 bg-[#121C2F] overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Tijdlijn</div>
          <div className="text-slate-100 font-medium">05:00 – 00:00</div>
        </div>
        <div className="text-xs text-slate-500">{dayEvents.length} {dayEvents.length === 1 ? "afspraak" : "afspraken"}</div>
      </div>
      <div className="relative overflow-y-auto max-h-[70vh]" data-testid="timeline">
        <div className="relative" style={{ height: totalHeight }}>
          {/* Hour grid */}
          {hours.map((h, idx) => (
            <div
              key={h}
              className="absolute left-0 right-0 flex"
              style={{ top: idx * HOUR_HEIGHT, height: HOUR_HEIGHT }}
            >
              <div className="w-16 shrink-0 pr-2 pt-0 text-right text-[11px] font-medium uppercase tracking-wider text-slate-500 border-r border-slate-800/70">
                {`${String(h % 24).padStart(2, "0")}:00`}
              </div>
              <div className="flex-1 border-b border-slate-800/50" />
            </div>
          ))}

          {/* Events */}
          <div className="absolute left-16 right-2 top-0" style={{ height: totalHeight }}>
            {laidOut.map((ev) => {
              const startMin = ev._s;
              const endMin = ev._e;
              const base = START_HOUR * 60;
              const top = Math.max(0, ((startMin - base) / 60) * HOUR_HEIGHT);
              const height = Math.max(28, ((endMin - startMin) / 60) * HOUR_HEIGHT - 4);
              const cat = catById(ev.category_id);
              const color = cat?.color || "#3B82F6";
              const laneWidthPct = 100 / ev.totalLanes;
              const leftPct = ev.lane * laneWidthPct;
              const isDragging = dragState?.id === ev.id;
              const isResizing = isDragging && dragState.mode === "resize";
              const dragOffsetPx = isDragging && !isResizing ? (dragState.deltaMin / 60) * HOUR_HEIGHT : 0;
              const previewStart = isDragging && !isResizing ? ev._s + dragState.deltaMin : ev._s;
              const previewEnd = isResizing ? ev._e + dragState.deltaMin : (isDragging ? ev._e + dragState.deltaMin : ev._e);
              const previewHeight = isResizing
                ? Math.max(28, ((previewEnd - ev._s) / 60) * HOUR_HEIGHT - 4)
                : height;
              const previewStartStr = `${String(Math.floor(previewStart / 60)).padStart(2, "0")}:${String(previewStart % 60).padStart(2, "0")}`;
              const previewEndStr = `${String(Math.floor(previewEnd / 60)).padStart(2, "0")}:${String(previewEnd % 60).padStart(2, "0")}`;
              const isRecurringOccurrence = String(ev.id).includes("::");
              return (
                <div
                  key={ev.id}
                  data-testid={`event-block-${ev.id}`}
                  className={`absolute rounded-lg group transition-shadow hover:shadow-lg ${
                    isDragging ? "z-30 shadow-2xl" : ""
                  } ${isResizing ? "cursor-ns-resize" : isDragging ? "cursor-grabbing" : "cursor-grab"}`}
                  style={{
                    top,
                    height: previewHeight,
                    left: `calc(${leftPct}% + 4px)`,
                    width: `calc(${laneWidthPct}% - 8px)`,
                    background: `${color}18`,
                    borderLeft: `3px solid ${color}`,
                    border: `1px solid ${color}33`,
                    transform: isDragging && !isResizing ? `translateY(${dragOffsetPx}px) scale(1.02)` : undefined,
                    transition: isDragging ? "none" : "transform 0.15s ease",
                  }}
                  onMouseDown={(e) => {
                    if (e.button !== 0) return;
                    if (e.target.closest("button")) return;
                    if (e.target.closest("[data-role=resize-handle]")) return;
                    startDrag(ev, e.clientY, e, "move");
                  }}
                  onTouchStart={(e) => {
                    if (e.target.closest("button")) return;
                    if (e.target.closest("[data-role=resize-handle]")) return;
                    if (e.touches[0]) startDrag(ev, e.touches[0].clientY, null, "move");
                  }}
                >
                  <div className="p-2.5 h-full flex flex-col justify-between overflow-hidden">
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1 min-w-0">
                          <DotsSixVertical size={12} className="text-slate-500 opacity-0 group-hover:opacity-100 shrink-0" weight="bold" />
                          <div className="text-sm font-semibold text-slate-50 truncate">{ev.title}</div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                          <button
                            data-testid={`event-edit-${ev.id}`}
                            onClick={(e) => { e.stopPropagation(); setEditEvent(ev); }}
                            className="text-slate-300 hover:text-slate-50 p-1 rounded"
                          ><PencilSimple size={12} /></button>
                          <button
                            data-testid={`event-delete-${ev.id}`}
                            onClick={(e) => { e.stopPropagation(); onDeleteRequest(ev); }}
                            className="text-slate-300 hover:text-rose-400 p-1 rounded"
                          ><Trash size={12} /></button>
                        </div>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                        {formatTime(previewStartStr)} – {formatTime(previewEndStr)}
                        {cat && <span className="ml-2" style={{ color }}>· {cat.name}</span>}
                        {ev.kind === "fixed" && <span className="ml-2 text-slate-500">· Vast</span>}
                        {ev.kind === "rest" && <span className="ml-2 text-indigo-400">· Rust</span>}
                        {isRecurringOccurrence && <span className="ml-2 text-slate-500">↻</span>}
                      </div>
                    </div>
                    {ev.location && previewHeight > 60 && (
                      <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-1 truncate">
                        <MapPin size={11} /> {ev.location}
                      </div>
                    )}
                  </div>
                  {/* Resize handle at bottom */}
                  <div
                    data-role="resize-handle"
                    data-testid={`event-resize-${ev.id}`}
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      e.stopPropagation();
                      startDrag(ev, e.clientY, e, "resize");
                    }}
                    onTouchStart={(e) => {
                      if (e.touches[0]) { e.stopPropagation(); startDrag(ev, e.touches[0].clientY, null, "resize"); }
                    }}
                    className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  >
                    <div className="w-8 h-1 rounded-full" style={{ background: color, opacity: 0.7 }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Current time line */}
          {isToday && currentTop() !== null && (
            <div
              className="absolute left-0 right-0 z-20 pointer-events-none"
              style={{ top: currentTop() }}
            >
              <div className="flex items-center">
                <div className="w-16 pr-2 text-right text-[10px] font-semibold text-violet-400 bg-[var(--nova-bg)] rounded px-1 shadow">
                  Nu
                </div>
                <div className="relative flex-1">
                  <div className="absolute -left-1.5 -top-1.5 w-3 h-3 rounded-full bg-violet-500 pulse-dot" />
                  <div className="h-px bg-violet-500/70" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <EventEditModal
        event={editEvent}
        open={!!editEvent}
        onOpenChange={(v) => !v && setEditEvent(null)}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#121C2F] border-slate-800 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-50">Herhalende afspraak verwijderen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {deleteTarget?.title} wordt {deleteTarget?.recurrence === "daily" ? "elke dag" : deleteTarget?.recurrence === "weekly" ? "elke week" : deleteTarget?.recurrence === "weekdays" ? "elke werkdag" : "herhaald"}. Wil je alleen deze keer overslaan op{" "}
              <span className="text-slate-200">{deleteTarget?.date}</span>, of de hele reeks verwijderen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="border-slate-800 bg-slate-900 hover:bg-slate-800 text-slate-200">Annuleren</AlertDialogCancel>
            <Button
              data-testid="skip-occurrence-button"
              onClick={() => skipOne(deleteTarget)}
              className="bg-slate-800 hover:bg-slate-700 text-slate-100"
            >
              Deze keer overslaan
            </Button>
            <Button
              data-testid="delete-series-button"
              onClick={() => deleteSeries(deleteTarget)}
              className="bg-rose-600 hover:bg-rose-500 text-white"
            >
              Reeks verwijderen
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
